import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { TranslationProvider } from './contexts/TranslationContext.tsx';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { AchievementNotificationProvider } from './contexts/AchievementNotificationContext.tsx';
import HomePage from './pages/HomePage.tsx';
import HomeEditorialPage from './pages/HomeEditorialPage.tsx';
import { resolveHomeLandingVariant } from './config/homeLanding.ts';
import SuggestProblemPage from './pages/SuggestProblemPage.tsx';
import TheoryPage from './pages/TheoryPage.tsx';
import ProblemPage from './pages/ProblemPage.tsx';
import RegistrationPage from './pages/RegistrationPage.tsx';
import VerifyEmailPage from './pages/VerifyEmailPage.tsx';
import LoginPage from './pages/LoginPage.tsx';
import ForgotPasswordPage from './pages/ForgotPasswordPage.tsx';
import ResetPasswordPage from './pages/ResetPasswordPage.tsx';
import Profile from './pages/Profile.tsx';
import SettingsPage from './pages/SettingsPage.tsx';
import SubscriptionPage from './pages/SubscriptionPage.tsx';
import DevelopmentPromoPage from './pages/DevelopmentPromoPage.tsx';
import SubscriptionCheckoutPage from './pages/SubscriptionCheckoutPage.tsx';
import LeaderboardPage from './pages/LeaderboardPage.tsx';
import AchievementsPage from './pages/AchievementsPage.tsx';
import CookiesPolicyPage from './pages/CookiesPolicyPage.tsx';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage.tsx';
import AnticheatPolicyPage from './pages/AnticheatPolicyPage.tsx';
import TermsPage from './pages/TermsPage.tsx';
import FaqPage from './pages/FaqPage.tsx';
import AdminPage from './pages/AdminPage.tsx';
import HrPage from './pages/HrPage.tsx';
import ClassPage from './pages/ClassPage.tsx';
import EducationPage from './pages/EducationPage.tsx';
import JoinClassPage from './pages/JoinClassPage.tsx';
import NotificationsPage from './pages/NotificationsPage.tsx';
import NotFoundPage from './pages/NotFoundPage.tsx';
import ProblemsTopicOrLegacy from './pages/ProblemsTopicOrLegacy.tsx';
import Navigation from './components/Navigation.tsx';
import Footer from './components/Footer.tsx';
import { ScrollToTop } from './components/ScrollToTop.tsx';
import { CookieConsentBanner } from './components/CookieConsentBanner.tsx';
import { initAppearanceTheme } from './utils/appearanceTheme.ts';
import { isNumericProblemIdParam } from './utils/problemUrls.ts';
import './App.css';

function AppShell() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const isAuthFullscreen =
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password';
  const isHomeLanding = location.pathname === '/';
  const homeLandingVariant = isHomeLanding
    ? resolveHomeLandingVariant(location.search)
    : 'classic';
  const isEditorialHome = homeLandingVariant === 'editorial';
  const isSubscription =
    location.pathname === '/subscription' ||
    location.pathname.startsWith('/subscription/checkout');
  const pathParts = location.pathname.split('/').filter(Boolean);
  const isProblemWorkspace =
    pathParts[0] === 'problems' &&
    pathParts.length === 3 &&
    pathParts[1] !== 'suggest' &&
    pathParts[1] !== 'create';
  const isClassWorkspace =
    location.pathname === '/my-class' ||
    location.pathname === '/join-class' ||
    pathParts[0] === 'class';
  const isProblemsList =
    location.pathname === '/problems' ||
    (pathParts[0] === 'problems' &&
      pathParts.length === 2 &&
      pathParts[1] !== 'suggest' &&
      pathParts[1] !== 'create' &&
      !isNumericProblemIdParam(pathParts[1]));

  useEffect(() => {
    if (loading) return;
    const userId = user?.id != null ? Number(user.id) : null;
    initAppearanceTheme(Number.isFinite(userId) ? userId : null);
  }, [user?.id, loading]);

  return (
    <div
      className={`app-container${isProblemWorkspace ? ' app-container--problem-fullscreen' : ''}${
        isHomeLanding ? ' app-container--landing' : ''
      }${isEditorialHome ? ' app-container--landing-editorial' : ''}${
        isProblemsList ? ' app-container--problems-list' : ''
      }`}
    >
      <ScrollToTop />
      {!isAuthFullscreen && !isProblemWorkspace && <Navigation />}
      <main
        className={`content-wrap${
          isAuthFullscreen ? ' content-wrap--login-full' : ''
        }${isHomeLanding ? ' content-wrap--landing' : ''}${
          isSubscription ? ' content-wrap--subscription' : ''
        }${isProblemsList ? ' content-wrap--problems' : ''}${
          isProblemWorkspace ? ' content-wrap--problem-workspace' : ''
        }`}
      >
              <Routes>
                <Route
                  path="/"
                  element={isEditorialHome ? <HomeEditorialPage /> : <HomePage />}
                />
                <Route path="/problems/suggest" element={<SuggestProblemPage />} />
                <Route path="/problems/create" element={<SuggestProblemPage />} />
                <Route path="/theory" element={<TheoryPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/achievements" element={<AchievementsPage />} />
                <Route path="/achievements/:userId" element={<AchievementsPage />} />
                <Route path="/problems/:topic/:slug" element={<ProblemPage />} />
                <Route path="/problems/:topicOrId?" element={<ProblemsTopicOrLegacy />} />
                <Route path="/register" element={<RegistrationPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/profile/:username" element={<Profile />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/subscription" element={<SubscriptionPage />} />
                <Route path="/promo/development" element={<DevelopmentPromoPage />} />
                <Route path="/subscription/checkout" element={<SubscriptionCheckoutPage />} />
                <Route path="/cookies-policy" element={<CookiesPolicyPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/anticheat-policy" element={<AnticheatPolicyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/hr" element={<HrPage />} />
                <Route path="/my-class" element={<EducationPage />} />
                <Route path="/class/:id" element={<ClassPage />} />
                <Route path="/join-class" element={<JoinClassPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/404" element={<NotFoundPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
      </main>
      {!isAuthFullscreen && !isProblemWorkspace && !isClassWorkspace && !isEditorialHome && (
        <Footer />
      )}
      {!isAuthFullscreen && <CookieConsentBanner />}
    </div>
  );
}

function App() {
  return (
    <TranslationProvider>
      <AuthProvider>
        <AchievementNotificationProvider>
        <Router>
          <AppShell />
        </Router>
        </AchievementNotificationProvider>
      </AuthProvider>
    </TranslationProvider>
  );
}

export default App;